import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type DisplayLocale = "en" | "zh-Hans";

const LOCALE_STORAGE_KEY = "gamenews.locale";

type LocaleContextValue = {
  locale: DisplayLocale;
  setLocale: (locale: DisplayLocale) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function isDisplayLocale(value: unknown): value is DisplayLocale {
  return value === "en" || value === "zh-Hans";
}

function initialLocale(): DisplayLocale {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isDisplayLocale(stored) ? stored : "en";
  } catch {
    return "en";
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, updateLocale] = useState<DisplayLocale>(initialLocale);

  const setLocale = useCallback((nextLocale: DisplayLocale) => {
    updateLocale(nextLocale);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    } catch {
      // Storage can be unavailable in private or locked-down browser contexts.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    const syncAcrossTabs = (event: StorageEvent) => {
      if (event.key === LOCALE_STORAGE_KEY && isDisplayLocale(event.newValue)) {
        updateLocale(event.newValue);
      }
    };
    window.addEventListener("storage", syncAcrossTabs);
    return () => window.removeEventListener("storage", syncAcrossTabs);
  }, []);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) {
    throw new Error("useLocale must be used inside LocaleProvider");
  }
  return value;
}

export function LocaleToggle({ className = "" }: { className?: string }) {
  const { locale, setLocale } = useLocale();
  const label = locale === "zh-Hans" ? "选择显示语言" : "Choose display language";

  return (
    <div className={`locale-toggle ${className}`.trim()} role="group" aria-label={label}>
      <button
        type="button"
        className={locale === "en" ? "active" : ""}
        onClick={() => setLocale("en")}
        aria-pressed={locale === "en"}
        lang="en"
      >
        EN
      </button>
      <button
        type="button"
        className={locale === "zh-Hans" ? "active" : ""}
        onClick={() => setLocale("zh-Hans")}
        aria-pressed={locale === "zh-Hans"}
        lang="zh-Hans"
      >
        中文
      </button>
    </div>
  );
}
